// Process handler for feedback data fetching
import { State } from '../../hooks/useAppState'
import { ApiResponse } from '../types'
import { DataPoints } from '../../types/FeedbackTypes'
import { IndividualSessionFeedbackData } from '../../types/IndividualSessionFeedbackTypes'
import { loadCloudFunctionsHandler } from '../handlers'

// Use new DataPoints structure from FeedbackTypes
export type FeedbackData = DataPoints

export const getLatestSessionFeedback = async (_state: State): Promise<ApiResponse<FeedbackData>> => {
  try {
    // For now, return mock data from public/feedback/latestSession.mock.json
    const response = await fetch('/feedback/latestSession.mock.json')

    if (!response.ok) {
      throw new Error(`Failed to fetch mock feedback data: ${response.statusText}`)
    }

    const data = await response.json()

    return {
      data,
      success: true
    }
  } catch (error: unknown) {
    console.error('❌ [ProcessHandler] Error fetching feedback data:', error)
    return {
      data: {
        datapoints: {
          teamData: {
            profileData: { pathSelected: { value: 0 }, timeParalyzed: { value: 0 }, inactiveTimeOutside: { value: 0 } },
            planningExecutingEvaluating: { pointsOfInterest: [] },
            teamPsychologicalSafety: { pointsOfInterest: [] },
            taskPerformance: { pointsOfInterest: [] },
            communicationClimate: { pointsOfInterest: [] },
            improvementAreas: { pointsOfInterest: [] },
            strengths: { pointsOfInterest: [] }
          },
          individual: {
            sessionProfile: { pathSelected: { value: 0 }, timeParalyzed: { value: 0 } },
            experience: { pointsOfInterest: [] },
            improvementAreas: { pointsOfInterest: [] },
            strengths: { pointsOfInterest: [] }
          }
        }
      },
      success: false,
      error: (error instanceof Error ? error.message : String(error)) || 'Failed to fetch feedback data'
    }
  }
}

/**
 * Get latest feedback data for current gamer
 * Returns simplified feedback data with individual insights including:
 * - topStrengths, improvementIdea, whatIsWorkingWell, whatToWatchOut,
 *   communicatedEmotion, and achievementsUnlocked
 */
export const getLatestFeedbackDataForGamer = async (
  state: State
): Promise<ApiResponse<IndividualSessionFeedbackData | undefined>> => {
  try {
    const cloudFunctions = await loadCloudFunctionsHandler()
    const response = await cloudFunctions.getLatestFeedbackDataForGamer(state)

    if (!response.success || !response.data) {
      console.warn('⚠️ [ProcessHandler] No feedback data available')
      return {
        data: undefined,
        success: false,
        error: 'No feedback data available'
      }
    }

    return {
      data: response.data as IndividualSessionFeedbackData,
      success: true
    }
  } catch (error: unknown) {
    console.error('❌ [ProcessHandler] Error fetching feedback data:', error)
    const message = error instanceof Error ? error.message : 'Failed to fetch feedback data'
    return {
      data: undefined,
      success: false,
      error: message
    }
  }
}

/**
 * Get swimmer feedback data for current gamer
 * Returns detailed feedback data with full swimmer format including all metrics and visualizations
 */
export const getSwimmerTeamDataForGamer = async (state: State): Promise<ApiResponse<unknown>> => {
  try {
    const cloudFunctions = await loadCloudFunctionsHandler()
    const response = await cloudFunctions.getSwimmerTeamDataForGamer(state)

    if (!response.success || !response.data) {
      console.warn('⚠️ [ProcessHandler] No swimmer data available')
      return {
        data: undefined,
        success: false,
        error: 'No swimmer data available'
      }
    }

    return {
      data: response.data,
      success: true
    }
  } catch (error: unknown) {
    console.error('❌ [ProcessHandler] Error fetching swimmer data:', error)
    const message = error instanceof Error ? error.message : 'Failed to fetch swimmer data'
    return {
      data: undefined,
      success: false,
      error: message
    }
  }
}

/**
 * Get swimmer individual feedback data for current gamer
 * Returns detailed individual feedback data with full swimmer format including all metrics and visualizations
 */
export const getSwimmerIndividualDataForGamer = async (state: State): Promise<ApiResponse<unknown>> => {
  try {
    const cloudFunctions = await loadCloudFunctionsHandler()
    const response = await cloudFunctions.getSwimmerIndividualDataForGamer(state)

    if (!response.success || !response.data) {
      console.warn('⚠️ [ProcessHandler] No swimmer individual data available')
      return {
        data: undefined,
        success: false,
        error: 'No swimmer individual data available'
      }
    }

    return {
      data: response.data,
      success: true
    }
  } catch (error: unknown) {
    console.error('❌ [ProcessHandler] Error fetching swimmer individual data:', error)
    const message = error instanceof Error ? error.message : 'Failed to fetch swimmer individual data'
    return {
      data: undefined,
      success: false,
      error: message
    }
  }
}
